/**
 * ExportMenu — bouton "Exporter ▾" avec menu déroulant Excel/CSV/PDF.
 * Applique automatiquement les filtres passés en prop.
 */
import { useState, useEffect, useRef } from "react";
import { colors, fonts } from "../styles/tokens";
import { exportsApi } from "../services/api";
import useToast from "../hooks/useToast";
import Button from "./Button";

const FORMATS = [
  { id: "excel", label: "Exporter en Excel", icon: "📊", method: "excel" },
  { id: "csv",   label: "Exporter en CSV",   icon: "📄", method: "csv" },
  { id: "pdf",   label: "Exporter en PDF",   icon: "🖨", method: "pdf" },
];

export default function ExportMenu({ filters = {} }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleExport = async (format) => {
    setBusy(format.id);
    setOpen(false);

    // Nettoie les filtres "all"/vides avant l'envoi
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v && v !== "all")
    );

    const toastId = toast.info(`Export ${format.label.split(" ").pop()} en cours…`, { duration: 0 });

    try {
      const res = await exportsApi[format.method](params);
      const filename = exportsApi.triggerDownload(res, `membres.${format.id === "excel" ? "xlsx" : format.id}`);
      toast.dismissToast(toastId);
      toast.success(`Téléchargement : ${filename}`);
    } catch (err) {
      toast.dismissToast(toastId);
      toast.error(err.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <Button
        variant="secondary"
        onClick={() => setOpen((v) => !v)}
        loading={!!busy}
        disabled={!!busy}
      >
        📤 Exporter ▾
      </Button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 220,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            padding: "6px",
            zIndex: 50,
            fontFamily: fonts.body,
          }}
        >
          {FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => handleExport(f)}
              role="menuitem"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                fontSize: "14px",
                color: colors.text,
                background: "transparent",
                textAlign: "left",
                transition: "background 120ms",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: "18px" }}>{f.icon}</span>
              <span>{f.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
